// src/components/ArabicKeyboard.js
//
// Small on-screen Arabic keyboard used next to any Arabic input field.
// Rendered inside a Popover anchored to the "AR" button. The keyboard
// closes automatically when focus moves elsewhere (the parent popover
// handles click-outside and the blur-on-host handler here handles
// switching to another field).

import React from 'react';
import { Box, Button, IconButton, Popover, Stack } from '@mui/material';
import { Backspace, SpaceBar } from '@mui/icons-material';

// Arabic alphabet + common variants. Ordered in rows the way a reader
// would expect — alphabet first, hamza/alef variants underneath, taa
// marbuta + alef maqsura at the end. No diacritics / numerals per the
// "letters only + space" spec.
const ROWS = [
  ['ض', 'ص', 'ث', 'ق', 'ف', 'غ', 'ع', 'ه', 'خ', 'ح', 'ج'],
  ['ش', 'س', 'ي', 'ب', 'ل', 'ا', 'ت', 'ن', 'م', 'ك'],
  ['ظ', 'ط', 'ذ', 'د', 'ز', 'ر', 'و', 'ة', 'ى'],
  ['ء', 'أ', 'إ', 'آ', 'ؤ', 'ئ'],
];

const ArabicKeyboard = ({ anchorEl, open, onClose, onInsert, onBackspace }) => {
  const handleKey = (ch) => () => onInsert(ch);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      disableAutoFocus
      disableEnforceFocus
      disableRestoreFocus
      slotProps={{
        paper: {
          sx: {
            p: 1,
            mt: 0.5,
            ml: '-50px',
            borderRadius: 2,
            direction: 'rtl',
            fontFamily: 'var(--sacred-font-body)',
          },
        },
      }}
    >
      <Stack spacing={0.5} sx={{ minWidth: 340 }}>
        {ROWS.map((row, ri) => (
          <Stack key={ri} direction="row" spacing={0.5} justifyContent="center">
            {row.map((ch) => (
              <Button
                key={ch}
                size="small"
                variant="outlined"
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleKey(ch)}
                sx={{
                  minWidth: 30,
                  px: 1,
                  py: 0.5,
                  fontFamily: 'var(--sacred-font-body)',
                  fontSize: '1.1rem',
                  lineHeight: 1.2,
                }}
              >
                {ch}
              </Button>
            ))}
          </Stack>
        ))}
        <Stack direction="row" spacing={0.5} justifyContent="center" sx={{ pt: 0.5 }}>
          <Button
            size="small"
            variant="contained"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onInsert(' ')}
            startIcon={<SpaceBar fontSize="small" />}
            sx={{ flex: 1, fontFamily: 'var(--sacred-font-body)' }}
          >
            مسافة
          </Button>
          <IconButton
            size="small"
            onMouseDown={(e) => e.preventDefault()}
            onClick={onBackspace}
            aria-label="backspace"
          >
            <Backspace fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>
      <Box sx={{ display: 'none' }} />
    </Popover>
  );
};

export default ArabicKeyboard;
